import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthActions, useAuthState } from '@/shared/stores/auth-store';
import { useNotificationActions } from '@/shared/stores/notification-store';
import { loginSchema, type LoginFormData } from '@/shared/schemas';
import { Button } from '@/shared/components';

/**
 * Страница входа в админ-панель
 * Содержит форму аутентификации с валидацией
 */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, clearError } = useAuthActions();
  const { isLoading } = useAuthState();
  const { showError } = useNotificationActions();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: 'admin1@alenushka-pediatr.ru',
      password: ''
    }
  });

  /**
   * Обработчик отправки формы
   * @param data - Данные формы
   */
  const onSubmit = async (data: LoginFormData): Promise<void> => {
    try {
      clearError(); // Очищаем предыдущие ошибки
      await login(data);

      // Перенаправляем на страницу, с которой пришел пользователь, или на dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error: any) {
      // Показываем уведомление об ошибке
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Ошибка входа в систему';
      showError(errorMessage);
      console.error('Login error:', error);
    }
  };

  /**
   * Переключение видимости пароля
   */
  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-base-200 to-secondary/10 flex items-center justify-center p-4">
      {/* Фоновые элементы */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Основная карточка входа */}
        <div className="card card-bordered bg-base-100 shadow-xl">
          <div className="card-body p-2">
            {/* Логотип и заголовок */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
                <i className="fas fa-stethoscope text-2xl text-primary-content"></i>
              </div>
              <h1 className="text-2xl font-bold text-base-content mb-2">
                Алёнушка
              </h1>
              <p className="text-base-content/60">Админ-панель</p>
            </div>

            {/* Форма входа */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Поле email */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    <i className="fas fa-envelope mr-2 text-primary"></i>
                    Email адрес
                  </span>
                </label>
                <input
                  {...register('email')}
                  type="email"
                  placeholder="admin1@alenushka-pediatr.ru"
                  className={`input input-bordered w-full ${errors.email ? 'input-error' : ''}`}
                  autoComplete="email"
                  disabled={isLoading || isSubmitting}
                />
                {errors.email && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.email.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Поле пароля */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    <i className="fas fa-lock mr-2 text-primary"></i>
                    Пароль
                  </span>
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Введите пароль"
                    className={`input input-bordered w-full pr-12 ${errors.password ? 'input-error' : ''}`}
                    autoComplete="current-password"
                    disabled={isLoading || isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-base-content/60 hover:text-base-content transition-colors duration-200"
                    disabled={isLoading || isSubmitting}
                  >
                    <i
                      className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                    ></i>
                  </button>
                </div>
                {errors.password && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.password.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Кнопка входа */}
              <Button
                htmlType="submit"
                variant="solid"
                color="primary"
                loading={isLoading || isSubmitting}
                block
                icon={<i className="fas fa-sign-in-alt"></i>}
              >
                {isLoading || isSubmitting ? 'Вход в систему...' : 'Войти'}
              </Button>
            </form>

            {/* Дополнительная информация */}
            <div className="mt-6 text-center">
              <p className="text-xs text-base-content/50">
                Педиатрическая клиника "Алёнушка" © 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
